/**
 * Item Library Integration for Scaffold
 * Maps field names to rates from Item Library API
 * Handles dynamic fields where codes come from user selections
 */

import { ItemLibraryResponse, ItemLibraryItem } from '../api/itemLibrary';
import { SCAFFOLD_REFERENCES } from './scaffoldReferenceMapping';
import { SCAFFOLD_FIELD_LABELS, SCAFFOLD_FIELD_UNITS } from './scaffoldFieldMapping';
import { EVALUATE_CODES, EvaluateCode } from './constants';

export interface RateInfo {
  rate: number;
  description: string;
  unit: string;
  resourceCode?: string;
}

/**
 * GWALP006 (MOT Type 1) and GWALP004 (Crush 6F2) are m³ rates.
 * Convert to m² by multiplying by the thickness in metres.
 *
 * scaffoldDimensionValues can hold two formats depending on how data arrived:
 *   - Manual config modal (zeroIntegerOnly NumberInput): "0.080"  → already metres
 *   - Excel import (extracts leading number from header "80mm Type1"): "80" → millimetres
 *
 * Rule: value >= 1 → treat as mm, divide by 1000; value < 1 → already metres.
 */
const M3_RATE_FIELDS = new Set([
  'scaffoldType1', 'loadingType1', 'stairType1',
  'scaffoldCrush6F2', 'loadingCrush6F2', 'stairCrush6F2',
]);
export const SCAFFOLD_M3_CONVERSION_FIELDS: ReadonlySet<string> = M3_RATE_FIELDS;

function parseThicknessValue(raw: string): number | null {
  const match = raw.trim().match(/^(\d+(?:\.\d+)?)/);
  return match ? parseFloat(match[1]) : null;
}

function getM3ToM2Multiplier(
  fieldName: string,
  dimensionValues: Record<string, string> | undefined,
  columnHeaders?: Record<string, string>,
): number {
  if (!M3_RATE_FIELDS.has(fieldName)) return 1;

  // Try explicit dimension value first (set by config modal or Excel extraction)
  const raw = dimensionValues?.[fieldName];
  if (raw) {
    const value = parseThicknessValue(raw);
    if (value && value > 0) {
      // >= 1 means mm (Excel import) → convert to metres; < 1 already in metres (manual entry)
      return value >= 1 ? value / 1000 : value;
    }
  }

  // Fall back to extracting the leading mm dimension from the column header text
  // e.g. "80mm Type1 (m²)" → 80 → 0.080; "xxmm Type1" → null → no conversion
  const headerText = columnHeaders?.[fieldName];
  if (headerText) {
    const value = parseThicknessValue(headerText);
    if (value && value > 0) {
      return value >= 1 ? value / 1000 : value;
    }
  }

  return 1;
}

/**
 * Get rate from item library by resource code
 */
function getRateByCode(
  resourceCode: string,
  resourceCodeMap: Map<string, ItemLibraryItem>,
  fieldName: string,
  fallbackDescription?: string,
): RateInfo {
  const normalizedCode = resourceCode.trim().toUpperCase();
  const item = resourceCodeMap.get(normalizedCode);

  if (item) {
    return {
      rate: item.nettRate || item.baseRate || 0,
      description: item.resourceDescription || fallbackDescription || fieldName,
      unit: item.unit || 'm²',
      resourceCode: normalizedCode,
    };
  }

  // Item not found - use fallback description if provided
  return {
    rate: 0,
    description: fallbackDescription || fieldName,
    unit: 'm²',
    resourceCode: '',
  };
}

/**
 * Create reverse mapping from label to code for EVALUATE_CODES
 */
function createLabelToCodeMapping(): Record<string, string> {
  const labelToCode: Record<string, string> = {};

  Object.entries(EVALUATE_CODES).forEach(([code, info]) => {
    if (info.label) {
      labelToCode[info.label] = code;
    }
  });

  return labelToCode;
}

const LABEL_TO_CODE = createLabelToCodeMapping();

/**
 * Get evaluate code from label
 * @param labelOrCode - Could be either a label ("100mm thick AC 32mm dense bin") or code ("ALP042")
 */
function getCodeFromLabelOrCode(labelOrCode: string): { code: string; description: string } | undefined {
  const trimmed = labelOrCode.trim();

  // Check if it's already a code (starts with ALP or GWALP)
  if (trimmed.startsWith('ALP') || trimmed.startsWith('GWALP')) {
    const evaluateCode = trimmed as EvaluateCode;
    if (EVALUATE_CODES[evaluateCode]) {
      return {
        code: trimmed,
        description: EVALUATE_CODES[evaluateCode].label
      };
    }
  }

  // Otherwise, reverse-lookup the code from the label
  const code = LABEL_TO_CODE[trimmed];
  if (code) {
    return {
      code,
      description: trimmed
    };
  }

  return undefined;
}

/**
 * Transform scaffoldDimensionValues to semantic field format with code and description
 * @param scaffoldDimensionValues - Dimension values (semantic field names as keys)
 * @returns Semantic field-based dimension values with code and description
 */
function transformDimensionValuesToSemanticFormat(
  scaffoldDimensionValues: Record<string, string>
): Record<string, { code: string; description: string }> {
  const semanticDimensions: Record<string, { code: string; description: string }> = {};

  // Scaffold uses semantic field names directly (no column mapping needed)
  Object.entries(scaffoldDimensionValues).forEach(([fieldName, value]) => {
    if (value && typeof value === 'string' && value.trim() !== '') {
      const codeInfo = getCodeFromLabelOrCode(value);
      if (codeInfo) {
        semanticDimensions[fieldName] = codeInfo;
      }
    }
  });

  return semanticDimensions;
}

/**
 * Get dynamic code info for a field from transformed dimension values
 * @param fieldName - Semantic field name (e.g., 'scaffoldCrush6F2')
 * @param semanticDimensions - Transformed dimension values in semantic format
 */
function getDynamicCodeInfo(
  fieldName: string,
  semanticDimensions: Record<string, { code: string; description: string }>
): { code: string; description: string } | undefined {
  return semanticDimensions[fieldName];
}

/**
 * Create a rate lookup map from item library for Scaffold
 * @param itemLibraryData - Item library response from API
 * @param groundworksData - Groundworks data for dynamic field values (optional)
 */
export function createScaffoldRateLookupMap(
  itemLibraryData: ItemLibraryResponse | undefined,
  groundworksData?: any,
): Map<string, RateInfo> {
  const rateMap = new Map<string, RateInfo>();

  if (!itemLibraryData) {
    return rateMap;
  }

  // Create a map of resourceCode to item for quick lookup
  const resourceCodeMap = new Map<string, ItemLibraryItem>();
  itemLibraryData.forEach((item: ItemLibraryItem) => {
    const code = item.resourceCode?.trim().toUpperCase();
    if (code) {
      resourceCodeMap.set(code, item);
    }
  });

  // Transform dimension values from semantic format upfront
  const semanticDimensions = groundworksData?.scaffoldDimensionValues
    ? transformDimensionValuesToSemanticFormat(groundworksData.scaffoldDimensionValues)
    : {};

  const dimensionValues = groundworksData?.scaffoldDimensionValues as Record<string, string> | undefined;
  const columnHeaders = groundworksData?.scaffoldColumnHeaders as Record<string, string> | undefined;

  // Map each field name to its rate info (rate, description, unit)
  Object.entries(SCAFFOLD_REFERENCES).forEach(([fieldName, reference]) => {
    // Get proper label from field mapping as fallback
    const fallbackDescription = SCAFFOLD_FIELD_LABELS[fieldName] || fieldName;

    // Check for dynamic code first (from user selections)
    const dynamicCodeInfo = getDynamicCodeInfo(fieldName, semanticDimensions);

    if (dynamicCodeInfo) {
      const rateInfo = getRateByCode(
        dynamicCodeInfo.code,
        resourceCodeMap,
        fieldName,
        dynamicCodeInfo.description
      );
      const m3ToM2 = getM3ToM2Multiplier(fieldName, dimensionValues, columnHeaders);
      rateMap.set(fieldName, {
        ...rateInfo,
        rate: rateInfo.rate * m3ToM2,
        unit: fieldName in SCAFFOLD_FIELD_UNITS ? SCAFFOLD_FIELD_UNITS[fieldName] : rateInfo.unit,
        // Always store the code even when not found in the library
        resourceCode: rateInfo.resourceCode || dynamicCodeInfo.code,
      });
      return;
    }

    if (!reference.code) {
      rateMap.set(fieldName, {
        rate: 0,
        description: fallbackDescription,
        unit: SCAFFOLD_FIELD_UNITS[fieldName] ?? '',
      });
      return;
    }

    // Static code from reference mapping
    const rateInfo = getRateByCode(reference.code, resourceCodeMap, fieldName, fallbackDescription);
    const m3ToM2 = getM3ToM2Multiplier(fieldName, dimensionValues, columnHeaders);
    rateMap.set(fieldName, {
      ...rateInfo,
      rate: rateInfo.rate * m3ToM2,
      unit: fieldName in SCAFFOLD_FIELD_UNITS ? SCAFFOLD_FIELD_UNITS[fieldName] : rateInfo.unit,
      // Always store the code even when not found in the library
      resourceCode: rateInfo.resourceCode || reference.code,
    });
  });

  return rateMap;
}

/**
 * Get rate info for a specific field name
 */
export function getScaffoldRateInfo(
  fieldName: string,
  rateLookupMap: Map<string, RateInfo>,
): RateInfo {
  return rateLookupMap.get(fieldName) || { rate: 0, description: fieldName, unit: SCAFFOLD_FIELD_UNITS[fieldName] ?? '' };
}

/**
 * Apply rates and descriptions to Scaffold summary items
 */
export function applyScaffoldRatesToSummary(
  summaryItems: any[],
  rateLookupMap: Map<string, RateInfo>,
): any[] {
  return summaryItems.map((item) => {
    const rateInfo = getScaffoldRateInfo(item.fieldName, rateLookupMap);
    const amount = Math.abs(item.quantity * rateInfo.rate);

    return {
      ...item,
      rate: rateInfo.rate,
      description: item.description,
      unit: rateInfo.unit,
      amount,
      resourceCode: rateInfo.resourceCode,
    };
  });
}
